import React, { useState } from "react";
import {
  Chip,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import SaveIcon from "@mui/icons-material/Save";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { formatMoney, formatPct } from "../../utils/money";

// A £0 trip cost (e.g. one saved blank) isn't worth a "+ £0.00 / trip" row.
const hasTrip = (line) => line.tripCost != null && Number(line.tripCost) !== 0;

function sortLines(lines) {
  return [...(lines || [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

function isExemptCatalogueLine(line, quoteMarkupPct) {
  if (line.isAdHoc || line.sellPriceSet || line.unitPriceOverridden) return false;
  const q = Number(quoteMarkupPct);
  const m = Number(line.markupPct);
  return Number.isFinite(q) && q > 0 && m === 0;
}

function EditableCell({ value, onCommit, type = "number", disabled }) {
  const [v, setV] = useState(value ?? "");
  const dirty = String(v) !== String(value ?? "");
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <TextField
        size="small" type={type}
        value={v}
        disabled={disabled}
        onChange={(e) => setV(e.target.value)}
        sx={{ width: 110 }}
      />
      <Tooltip title="Save">
        <span>
          <IconButton
            size="small"
            disabled={disabled || !dirty}
            color={dirty ? "primary" : "default"}
            onClick={() => onCommit(v)}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

export default function LinesTable({
  quote,
  disabled,
  onUpdateLine,
  onDeleteLine,
}) {
  const lines = sortLines(quote.lines);

  const handleQtyOverride = async (line, newQty) => {
    await onUpdateLine(line.id, {
      quantityOverridden: true,
      quantity: String(newQty),
    });
  };
  const handleReleaseQty = async (line) => {
    await onUpdateLine(line.id, { quantityOverridden: false });
  };

  // For ad-hoc: edits (description / qty / unit price / unit cost) commit on save
  const handleAdHocFieldCommit = async (line, patch) => {
    await onUpdateLine(line.id, patch);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ mb: 2 }}>Lines</Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Unit Cost</TableCell>
              <TableCell align="right">Unit Price</TableCell>
              <TableCell align="right">Markup %</TableCell>
              <TableCell align="right">Total Cost</TableCell>
              <TableCell align="right">Total Price</TableCell>
              <TableCell>Type</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center">No lines yet — fill in the day grid or add an ad-hoc line.</TableCell></TableRow>
            ) : lines.map((line) => {
              const exempt = isExemptCatalogueLine(line, quote.markupPct);
              return (
              <TableRow key={line.id} hover sx={line.priceMissing ? { backgroundColor: (t) => t.palette.error.light + "22" } : undefined}>
                <TableCell>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {line.priceMissing && (
                      <Tooltip title="No available pricing for this product in the quote's location/season/slot">
                        <WarningAmberIcon fontSize="small" color="error" />
                      </Tooltip>
                    )}
                    {line.isAdHoc ? (
                      <AdHocDescriptionEditor
                        line={line}
                        disabled={disabled}
                        onCommit={(desc) => handleAdHocFieldCommit(line, { description: desc })}
                      />
                    ) : (
                      <Typography variant="body2">{line.description}</Typography>
                    )}
                  </Stack>
                  {line.basis && (
                    // How the quantity was worked out; for reps only, not on the client PDF.
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25, maxWidth: 360 }}>
                      {line.basis}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  {line.isAdHoc ? (
                    <EditableCell
                      value={line.quantity}
                      disabled={disabled}
                      onCommit={(v) => handleAdHocFieldCommit(line, { quantity: String(v) })}
                    />
                  ) : line.quantityOverridden ? (
                    <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
                      <EditableCell
                        value={line.quantity}
                        disabled={disabled}
                        onCommit={(v) => handleQtyOverride(line, v)}
                      />
                      <Tooltip title="Release override">
                        <span>
                          <IconButton size="small" onClick={() => handleReleaseQty(line)} disabled={disabled}>
                            <LockOpenIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  ) : (
                    <Stack direction="row" alignItems="center" spacing={0.5} justifyContent="flex-end">
                      <Typography variant="body2">{line.quantity}</Typography>
                      <Tooltip title="Override quantity">
                        <span>
                          <IconButton
                            size="small"
                            disabled={disabled}
                            onClick={() => handleQtyOverride(line, line.quantity)}
                          >
                            <LockIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  {line.isAdHoc ? (
                    <EditableCell
                      value={line.unitCost ?? ""}
                      disabled={disabled}
                      onCommit={(v) => handleAdHocFieldCommit(line, { unitCost: v === "" ? null : String(v) })}
                    />
                  ) : (
                    <Stack spacing={0.5} alignItems="flex-end">
                      <CostPart
                        label="/ head"
                        value={line.unitCost}
                        overridden={line.costOverridden}
                        disabled={disabled}
                        showLabel={hasTrip(line)}
                        lockTitle="Override this quote's per-unit cost"
                        onOverride={(v) => onUpdateLine(line.id, { costOverridden: true, unitCost: String(v) })}
                        onRelease={() => onUpdateLine(line.id, { costOverridden: false })}
                      />
                      {hasTrip(line) && (
                        <CostPart
                          label="/ trip"
                          prefix="+ "
                          value={line.tripCost}
                          overridden={line.tripCostOverridden}
                          disabled={disabled}
                          showLabel
                          lockTitle="Enter this group's coach cost per trip"
                          onOverride={(v) => onUpdateLine(line.id, { tripCostOverridden: true, tripCost: String(v) })}
                          onRelease={() => onUpdateLine(line.id, { tripCostOverridden: false })}
                        />
                      )}
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  {line.isAdHoc ? (
                    <EditableCell
                      value={line.unitPrice}
                      disabled={disabled}
                      onCommit={(v) => handleAdHocFieldCommit(line, { unitPrice: String(v) })}
                    />
                  ) : (
                    <Stack alignItems="flex-end">
                      <CostPart
                        value={line.unitPrice}
                        overridden={line.unitPriceOverridden}
                        disabled={disabled}
                        lockTitle="Set this line's unit price for this quote"
                        releaseTitle="Go back to the catalogue price"
                        onOverride={(v) => onUpdateLine(line.id, { unitPriceOverridden: true, unitPrice: String(v) })}
                        onRelease={() => onUpdateLine(line.id, { unitPriceOverridden: false })}
                      />
                    </Stack>
                  )}
                </TableCell>
                <TableCell align="right">
                  {line.markupPct == null ? "" : formatPct(line.markupPct)}
                </TableCell>
                <TableCell align="right">{line.totalCost != null ? formatMoney(line.totalCost) : ""}</TableCell>
                <TableCell align="right">{formatMoney(line.totalPrice)}</TableCell>
                <TableCell>
                  <Stack direction="row" gap={0.5} flexWrap="wrap">
                    <Chip
                      size="small"
                      label={line.isAdHoc ? "ad-hoc" : "catalogue"}
                      color={line.isAdHoc ? "default" : "primary"}
                      variant="outlined"
                    />
                    {line.unitPriceOverridden && (
                      <Tooltip title="Unit price set by hand for this quote; markup doesn't apply">
                        <Chip size="small" label="your price" color="secondary" variant="outlined" />
                      </Tooltip>
                    )}
                    {line.sellPriceSet && !line.unitPriceOverridden && (
                      <Tooltip title="Sells at a set price per unit; quote markup doesn't apply">
                        <Chip size="small" label="set price" color="success" variant="outlined" />
                      </Tooltip>
                    )}
                    {hasTrip(line) && (
                      <Tooltip title="Trip cost is added once for each time it's in the day grid">
                        <Chip size="small" label={`${line.trips || 1} × trip`} variant="outlined" />
                      </Tooltip>
                    )}
                    {exempt && (
                      <Tooltip title="Markup-exempt — passes through at cost regardless of quote markup">
                        <Chip size="small" label="exempt" color="warning" variant="outlined" />
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  {line.isAdHoc && (
                    <Tooltip title="Delete">
                      <span>
                        <IconButton size="small" disabled={disabled} onClick={() => onDeleteLine(line.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

/** One part of a catalogue line's cost (per head or per trip), with its own override lock. */
function CostPart({
  label, prefix = "", value, overridden, disabled, showLabel, lockTitle,
  releaseTitle = "Go back to the catalogue cost", onOverride, onRelease,
}) {
  if (overridden) {
    return (
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <EditableCell value={value ?? ""} disabled={disabled} onCommit={onOverride} />
        {showLabel && <Typography variant="caption" color="text.secondary">{label}</Typography>}
        <Tooltip title={releaseTitle}>
          <span>
            <IconButton size="small" onClick={onRelease} disabled={disabled}>
              <LockOpenIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant="body2" sx={{ whiteSpace: "nowrap" }}>
        {prefix}{formatMoney(value)}{showLabel ? ` ${label}` : ""}
      </Typography>
      <Tooltip title={lockTitle}>
        <span>
          <IconButton size="small" disabled={disabled} onClick={() => onOverride(value ?? 0)}>
            <LockIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}

function AdHocDescriptionEditor({ line, disabled, onCommit }) {
  const [v, setV] = useState(line.description || "");
  const dirty = v !== line.description;
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <TextField
        size="small"
        value={v}
        disabled={disabled}
        onChange={(e) => setV(e.target.value)}
        sx={{ minWidth: 200 }}
      />
      <Tooltip title="Save description">
        <span>
          <IconButton
            size="small"
            disabled={disabled || !dirty || !v.trim()}
            color={dirty ? "primary" : "default"}
            onClick={() => onCommit(v.trim())}
          >
            <SaveIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Stack>
  );
}
